import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

/**
 * ActiveNoteContext
 * --------------------------------------------------------------------------
 * Bridges the chat panel (HomePage) and the notebook editor inside AppShell.
 *
 *   - Notebook calls `setActiveNote({ id, title, getContext })` whenever the
 *     visible page changes. `getContext(maxChars)` lazily extracts plain text
 *     from the current page so the chat can avoid stale snapshots.
 *   - Notebook registers an `inserter(content, label)` for direct, in-process
 *     insertion of AI output into the active page (replaces the localStorage
 *     hop used by the legacy "Insert to Note" path).
 *   - Chat (HomePage) reads `activeNote` to show context badges, build
 *     note-aware prompts (Quick Start chips), and calls `insertIntoActiveNote`
 *     to embed responses without leaving the panel.
 *
 * Both `getContext` and the inserter are held in refs so updates to those
 * functions never cause consumers to re-render unnecessarily.
 */

const ActiveNoteContext = createContext(null);

export const useActiveNote = () => {
  const ctx = useContext(ActiveNoteContext);
  if (!ctx) {
    // Permissive: returns no-op shape so the hook is safe outside AppShell
    return NOOP;
  }
  return ctx;
};

const NOOP = {
  activeNote: null,
  setActiveNote: () => {},
  clearActiveNote: () => {},
  registerInserter: () => () => {},
  insertIntoActiveNote: () => false,
  getActiveNoteContext: () => "",
};

export const ActiveNoteProvider = ({ children }) => {
  const [activeNote, setActiveNoteState] = useState(null);
  const getContextRef = useRef(null);
  const inserterRef = useRef(null);

  const setActiveNote = useCallback((note) => {
    if (!note) {
      setActiveNoteState(null);
      getContextRef.current = null;
      return;
    }
    const { id, title, getContext } = note;
    getContextRef.current = typeof getContext === "function" ? getContext : null;
    setActiveNoteState({ id, title: title || "Untitled note" });
  }, []);

  const clearActiveNote = useCallback(() => {
    setActiveNoteState(null);
    getContextRef.current = null;
  }, []);

  /**
   * Notebook calls this on mount to register a direct insertion handler.
   * Returns an unregister fn for cleanup.
   */
  const registerInserter = useCallback((fn) => {
    inserterRef.current = typeof fn === "function" ? fn : null;
    return () => {
      if (inserterRef.current === fn) inserterRef.current = null;
    };
  }, []);

  /**
   * Chat calls this to embed AI output into the active note.
   * Returns true if a direct inserter handled it, false otherwise
   * (caller can fall back to localStorage).
   */
  const insertIntoActiveNote = useCallback((content, label) => {
    if (!inserterRef.current) return false;
    try {
      inserterRef.current(content, label);
      return true;
    } catch (e) {
      console.error("ActiveNote inserter threw:", e);
      return false;
    }
  }, []);

  /** Returns truncated plain text of the active note, or "" if none. */
  const getActiveNoteContext = useCallback((maxChars = 2000) => {
    if (!getContextRef.current) return "";
    try {
      return getContextRef.current(maxChars) || "";
    } catch (e) {
      console.error("ActiveNote getContext threw:", e);
      return "";
    }
  }, []);

  const value = {
    activeNote,
    setActiveNote,
    clearActiveNote,
    registerInserter,
    insertIntoActiveNote,
    getActiveNoteContext,
  };

  return (
    <ActiveNoteContext.Provider value={value}>
      {children}
    </ActiveNoteContext.Provider>
  );
};
